import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSession, logout } from "@/lib/auth";
import { sendChat } from "@/lib/llm";
import {
	Approach,
	APPROACH_LABELS,
	ChatMessage,
	ClientProfile,
	DEFAULT_PROFILE,
	PRESENTING_PROBLEM_LABELS,
	PresentingProblem,
	SEVERITY_LABELS,
	buildSystemPrompt,
	openingMessage,
} from "@/lib/session";
import "./chat.css";

let messageSeq = 0;
const nextId = () => `msg-${Date.now()}-${messageSeq++}`;

const Chat: React.FC = () => {
	const navigate = useNavigate();
	const [session, setSessionState] = useState(() => getSession());

	// Redirect to login if there is no mock session.
	useEffect(() => {
		if (!getSession()) {
			navigate("/login");
		}
	}, [navigate]);

	const [profile, setProfile] = useState<ClientProfile>(DEFAULT_PROFILE);
	const [showPrompt, setShowPrompt] = useState(false);
	const [sessionStarted, setSessionStarted] = useState(false);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);
	const [sendError, setSendError] = useState<string | null>(null);

	const transcriptRef = useRef<HTMLDivElement>(null);
	const abortRef = useRef<AbortController | null>(null);

	const systemPrompt = useMemo(() => buildSystemPrompt(profile), [profile]);

	// Keep the transcript scrolled to the newest message.
	useEffect(() => {
		if (transcriptRef.current) {
			transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
		}
	}, [messages]);

	const update = <K extends keyof ClientProfile>(
		key: K,
		value: ClientProfile[K],
	) => setProfile((prev) => ({ ...prev, [key]: value }));

	const startSession = () => {
		setMessages([
			{ id: nextId(), role: "client", text: openingMessage(profile), timestamp: Date.now() },
		]);
		setSessionStarted(true);
	};

	const endSession = () => {
		abortRef.current?.abort();
		setSessionStarted(false);
		setMessages([]);
		setSending(false);
		setSendError(null);
	};

	const sendMessage = async (e: React.FormEvent) => {
		e.preventDefault();
		const text = draft.trim();
		if (!text || !sessionStarted || sending) return;

		const clinicianMsg: ChatMessage = {
			id: nextId(),
			role: "clinician",
			text,
			timestamp: Date.now(),
		};
		const history = [...messages, clinicianMsg];
		setMessages(history);
		setDraft("");
		setSendError(null);
		setSending(true);

		const controller = new AbortController();
		abortRef.current = controller;

		// Send the full transcript + the side-panel-derived system prompt to the
		// proxy, which forwards it to the configured provider.
		const result = await sendChat({
			systemPrompt,
			messages: history,
			signal: controller.signal,
		});

		if (controller.signal.aborted) return;
		setSending(false);

		if (result.ok && result.reply) {
			setMessages((prev) => [
				...prev,
				{ id: nextId(), role: "client", text: result.reply!, timestamp: Date.now() },
			]);
		} else if (result.error !== "cancelled") {
			setSendError(result.error || "Something went wrong.");
		}
	};

	const handleLogout = () => {
		logout();
		navigate("/login");
	};

	const formatTime = (ts: number) =>
		new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

	return (
		<div className="chat-wrapper">
			{/* ---------- Session configuration side panel ---------- */}
			<aside className="chat-sidebar">
				<div className="sidebar-section">
					<h2 className="sidebar-heading">Session Setup</h2>
					<p className="sidebar-note">
						Configure the simulated client. These fields shape the system
						prompt sent to the model.
					</p>
				</div>

				<div className="field">
					<label htmlFor="client-name">Client Name</label>
					<input
						id="client-name"
						type="text"
						value={profile.name}
						disabled={sessionStarted}
						onChange={(e) => update("name", e.target.value)}
					/>
				</div>

				<div className="field-row">
					<div className="field">
						<label htmlFor="client-gender">Gender</label>
						<select
							id="client-gender"
							value={profile.gender}
							disabled={sessionStarted}
							onChange={(e) => update("gender", e.target.value as ClientProfile["gender"])}
						>
							<option value="female">Female</option>
							<option value="male">Male</option>
							<option value="non-binary">Non-binary</option>
						</select>
					</div>

					<div className="field">
						<label htmlFor="client-age">Age</label>
						<input
							id="client-age"
							type="number"
							min={18}
							max={99}
							value={profile.age}
							disabled={sessionStarted}
							onChange={(e) => update("age", Number(e.target.value))}
						/>
					</div>
				</div>

				<div className="field">
					<label htmlFor="presenting-problem">Presenting Problem</label>
					<select
						id="presenting-problem"
						value={profile.presentingProblem}
						disabled={sessionStarted}
						onChange={(e) =>
							update("presentingProblem", e.target.value as PresentingProblem)
						}
					>
						{Object.entries(PRESENTING_PROBLEM_LABELS).map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</div>

				<div className="field">
					<label htmlFor="severity">Symptom Severity</label>
					<input
						id="severity"
						type="range"
						min={0}
						max={2}
						step={1}
						value={["mild", "moderate", "severe"].indexOf(profile.severity)}
						disabled={sessionStarted}
						onChange={(e) =>
							update(
								"severity",
								(["mild", "moderate", "severe"] as const)[Number(e.target.value)],
							)
						}
					/>
					<div className="range-labels">
						<span>Mild</span>
						<span>Moderate</span>
						<span>Severe</span>
					</div>
				</div>

				<div className="field">
					<label htmlFor="approach">Clinical Approach</label>
					<select
						id="approach"
						value={profile.approach}
						disabled={sessionStarted}
						onChange={(e) => update("approach", e.target.value as Approach)}
					>
						{Object.entries(APPROACH_LABELS).map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</select>
				</div>

				<div className="field">
					<label htmlFor="session-length">Session Length (minutes)</label>
					<input
						id="session-length"
						type="number"
						min={15}
						max={90}
						step={5}
						value={profile.sessionLength}
						disabled={sessionStarted}
						onChange={(e) => update("sessionLength", Number(e.target.value))}
					/>
				</div>

				<div className="field">
					<label htmlFor="notes">Additional Background</label>
					<textarea
						id="notes"
						rows={3}
						placeholder="Optional context about the client..."
						value={profile.notes}
						disabled={sessionStarted}
						onChange={(e) => update("notes", e.target.value)}
					/>
				</div>

				<button
					type="button"
					className="prompt-toggle"
					onClick={() => setShowPrompt((v) => !v)}
				>
					{showPrompt ? "Hide" : "Show"} system prompt
				</button>

				{showPrompt && (
					<pre className="prompt-preview">{systemPrompt}</pre>
				)}

				{!sessionStarted ? (
					<button type="button" className="start-button" onClick={startSession}>
						Start Session
					</button>
				) : (
					<button type="button" className="end-button" onClick={endSession}>
						End Session
					</button>
				)}
			</aside>

			{/* ---------- Chat pane ---------- */}
			<main className="chat-main">
				<header className="chat-header">
					<div className="chat-header-info">
						<span className="chat-header-title">
							{profile.name} · {PRESENTING_PROBLEM_LABELS[profile.presentingProblem]}
						</span>
						<span className="chat-header-sub">
							{SEVERITY_LABELS[profile.severity]} severity · {profile.sessionLength} min ·{" "}
							{APPROACH_LABELS[profile.approach]}
						</span>
					</div>
					<button type="button" className="logout-button" onClick={handleLogout}>
						Sign out{session ? ` (${session.username})` : ""}
					</button>
				</header>

				<div className="chat-transcript" ref={transcriptRef}>
					{!sessionStarted && (
						<div className="chat-empty">
							<h3>Ready to begin</h3>
							<p>
								Configure the simulated client on the left, then press{" "}
								<strong>Start Session</strong> to begin the mock conversation.
							</p>
						</div>
					)}

					{messages.map((msg) => (
						<div key={msg.id} className={`message message-${msg.role}`}>
							<div className="message-bubble">
								<span className="message-role">
									{msg.role === "client" ? profile.name : "You"}
								</span>
								<p className="message-text">{msg.text}</p>
								<span className="message-time">{formatTime(msg.timestamp)}</span>
							</div>
						</div>
					))}

					{sending && (
						<div className="message message-client">
							<div className="message-bubble typing">
								<span className="message-role">{profile.name}</span>
								<span className="typing-dots">
									<span></span>
									<span></span>
									<span></span>
								</span>
							</div>
						</div>
					)}
				</div>

				{sendError && <div className="chat-send-error">{sendError}</div>}

				<form className="chat-input-bar" onSubmit={sendMessage}>
					<input
						type="text"
						placeholder={
							sessionStarted
								? sending
									? `${profile.name} is typing...`
									: "Respond to the client..."
								: "Start a session to begin typing"
						}
						value={draft}
						disabled={!sessionStarted || sending}
						onChange={(e) => setDraft(e.target.value)}
					/>
					<button type="submit" disabled={!sessionStarted || sending || !draft.trim()}>
						{sending ? "..." : "Send"}
					</button>
				</form>
			</main>
		</div>
	);
};

export default Chat;
